<template>
  <div>
    <DataTable
      :value="displayData"
      :paginator="!showSkeletonLoading"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items "
      :rows="effectivePaginationInfo?.pageSize || 20"
      :rowsPerPageOptions="[10, 20, 50, 100]"
      :sortable="!showSkeletonLoading"
      class="p-datatable-sm"
      stripedRows
      responsiveLayout="scroll"
      scrollable
      scrollHeight="calc(100vh - 17rem)"
      :sortField="currentSortField"
      :sortOrder="currentSortOrder"
      :totalRecords="effectivePaginationInfo?.total || 0"
      :lazy="true"
      :filterDisplay="showFilters ? 'row' : undefined"
      v-model:filters="tableFilters"
      @page="onPageChange"
      @sort="onSortChange"
      @filter="onFilterChange"
      @row-click="onRowClick"
      rowHover
      data-test-id="shopify-payouts-table"
    >
      <!-- Payout ID Column -->
      <Column
        field="Id"
        header="Payout ID"
        :sortable="true"
        :filter="showFilters"
        filterField="Id"
        :showFilterMenu="false"
        :showFilterMatchModes="false"
        :showFilterOperator="false"
        style="min-width: 6rem"
      >
        <template #body="{ data }">
          <div v-if="showSkeletonLoading">
            <Skeleton width="8rem" height="1rem"></Skeleton>
          </div>
          <div v-else class="flex items-center gap-2">
            <BanknotesIcon class="size-4 text-primary-400" />
            <span
              class="font-medium text-surface-700 hover:text-surface-900 dark:text-surface-0 dark:hover:text-surface-400 underline underline-offset-4 cursor-pointer"
            >
              {{ data.Id }}
            </span>
          </div>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            placeholder="Payout ID"
            class="p-column-filter w-full"
            @input="debouncedIdFilter(filterCallback)"
            data-test-id="payout-id-filter"
            size="small"
          />
        </template>
      </Column>

      <!-- Merchant Column (conditional) -->
      <Column
        v-if="showMerchantColumn"
        field="MerchantName"
        header="Merchant"
        :sortable="false"
        style="min-width: 10rem"
      >
        <template #body="{ data }">
          <div v-if="showSkeletonLoading">
            <Skeleton width="8rem" height="1rem"></Skeleton>
          </div>
          <span
            v-else-if="data.MerchantId"
            class="text-sm font-medium text-primary-600 hover:text-primary-800 dark:text-surface-0 dark:hover:text-surface-600 underline underline-offset-4 cursor-pointer"
            @click.stop="navigateToMerchantConfig(data.MerchantId)"
          >
            {{ data.MerchantName || '-' }}
          </span>
          <span
            v-else
            class="text-sm font-medium text-surface-900 dark:text-surface-0"
          >
            {{ data.MerchantName || '-' }}
          </span>
        </template>
      </Column>

      <!-- Amount Column -->
      <Column
        field="Amount"
        header="Amount"
        :sortable="false"
        style="min-width: 8rem"
      >
        <template #body="{ data }">
          <div v-if="showSkeletonLoading">
            <Skeleton width="5rem" height="1rem"></Skeleton>
          </div>
          <span
            v-else
            class="font-semibold text-surface-900 dark:text-surface-0"
          >
            {{ formatCurrency(data.Amount, data.Currency) }}
          </span>
        </template>
      </Column>

      <!-- Currency Column -->
      <Column
        field="Currency"
        header="Currency"
        :sortable="false"
        style="min-width: 5rem"
      >
        <template #body="{ data }">
          <div v-if="showSkeletonLoading">
            <Skeleton width="3rem" height="1rem"></Skeleton>
          </div>
          <span v-else class="text-sm text-surface-600 dark:text-surface-300">
            {{ data.Currency }}
          </span>
        </template>
      </Column>

      <!-- Transaction Type Column -->
      <Column
        field="TransactionType"
        header="Type"
        :sortable="false"
        style="min-width: 6rem"
      >
        <template #body="{ data }">
          <div v-if="showSkeletonLoading">
            <Skeleton width="4rem" height="1rem"></Skeleton>
          </div>
          <span v-else class="text-sm text-surface-600 dark:text-surface-300">
            {{ formatTransactionType(data.TransactionType) }}
          </span>
        </template>
      </Column>

      <!-- Store ID Column -->
      <Column
        field="StoreId"
        header="Store ID"
        :sortable="false"
        :filter="showFilters"
        filterField="StoreId"
        :showFilterMenu="false"
        :showFilterMatchModes="false"
        :showFilterOperator="false"
        style="min-width: 8rem"
      >
        <template #body="{ data }">
          <div v-if="showSkeletonLoading">
            <Skeleton width="6rem" height="1rem"></Skeleton>
          </div>
          <span
            v-else
            class="text-sm text-surface-600 dark:text-surface-300 font-mono"
          >
            {{ data.StoreId }}
          </span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <Select
            v-model="filterModel.value"
            :options="storeIdOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Store"
            class="p-column-filter w-full"
            @change="debouncedStoreIdFilter(filterCallback)"
            data-test-id="payout-store-id-filter"
            size="small"
            filter
            showClear
          />
        </template>
      </Column>

      <!-- Business Entity ID Column -->
      <Column
        field="BusinessEntityId"
        header="Business Entity"
        :sortable="false"
        :filter="showFilters"
        filterField="BusinessEntityId"
        :showFilterMenu="false"
        :showFilterMatchModes="false"
        :showFilterOperator="false"
        style="min-width: 8rem"
      >
        <template #body="{ data }">
          <div v-if="showSkeletonLoading">
            <Skeleton width="8rem" height="1rem"></Skeleton>
          </div>
          <span v-else class="text-sm text-surface-600 dark:text-surface-300">
            {{ getBusinessEntityDisplayName(data.BusinessEntityId) }}
          </span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <Select
            v-model="filterModel.value"
            :options="businessEntityIdOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Business entity"
            class="p-column-filter w-full"
            @change="debouncedBusinessEntityIdFilter(filterCallback)"
            data-test-id="payout-business-entity-id-filter"
            size="small"
            filter
            showClear
          />
        </template>
      </Column>
      <!-- Bank Account ID Column -->
      <Column
        field="BankAccountId"
        header="Bank Account"
        :sortable="false"
        :filter="showFilters"
        filterField="BankAccountId"
        :showFilterMenu="false"
        :showFilterMatchModes="false"
        :showFilterOperator="false"
        style="min-width: 12rem"
      >
        <template #body="{ data }">
          <div v-if="showSkeletonLoading">
            <Skeleton width="8rem" height="1rem"></Skeleton>
          </div>
          <template v-else>
            <span
              v-if="store.getBankAccountDisplayName(data, merchantId)"
              class="text-sm text-surface-600 dark:text-surface-300"
            >
              {{ store.getBankAccountDisplayName(data, merchantId) }}
            </span>
            <div
              v-else-if="data.BankAccountAssignmentState"
              class="flex items-center gap-2"
            >
              <BadgeBankAccountAssignmentState
                :state="data.BankAccountAssignmentState"
                :data-test-id="`bank-account-assignment-state-${data.Id}`"
              />
              <Button
                v-if="canAssignBankAccount(data)"
                @click.stop="openAssignmentDialog(data)"
                variant="link"
                data-test-id="assign-bank-account-btn"
                label="Assign"
                size="small"
              >
              </Button>
            </div>
            <span v-else class="text-surface-400 dark:text-surface-500 text-xs"
              >—</span
            >
          </template>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <Select
            v-model="filterModel.value"
            :options="bankAccountIdOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Bank account"
            class="p-column-filter w-full"
            @change="debouncedBankAccountIdFilter(filterCallback)"
            data-test-id="payout-bank-account-id-filter"
            size="small"
            filter
            showClear
          />
        </template>
      </Column>

      <!-- Status Column -->
      <Column
        field="Status"
        header="Status"
        :sortable="true"
        :filter="showFilters"
        filterField="Status"
        :showFilterMatchModes="false"
        :showFilterOperator="false"
        :showFilterMenu="false"
        style="min-width: 4rem"
      >
        <template #body="{ data }">
          <div v-if="showSkeletonLoading">
            <Skeleton
              width="5rem"
              height="1.5rem"
              borderRadius="12px"
            ></Skeleton>
          </div>
          <BadgePayoutStatus
            v-else
            :status="data.Status"
            :data-test-id="`payout-status-${data.Id}`"
          />
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect
            v-model="filterModel.value"
            :options="statusOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Status"
            class="p-column-filter w-full"
            :maxSelectedLabels="2"
            @change="debouncedStatusFilter(filterCallback)"
            data-test-id="payout-status-filter"
            size="small"
            filter
            showClear
          />
        </template>
      </Column>

      <!-- Issued At Column -->
      <Column
        field="IssuedAt"
        header="Issued At"
        :sortable="true"
        style="min-width: 10rem"
      >
        <template #body="{ data }">
          <div v-if="showSkeletonLoading">
            <Skeleton width="8rem" height="1rem"></Skeleton>
          </div>
          <span v-else class="text-xs text-surface-500 dark:text-surface-400">
            {{ formatDateTimeForDisplay(data.IssuedAt) }}
          </span>
        </template>
      </Column>

      <!-- Empty state -->
      <template #empty>
        <div
          v-if="!showSkeletonLoading"
          class="flex flex-col items-center justify-center py-12"
          data-test-id="payouts-empty-state"
        >
          <i
            class="pi pi-inbox text-4xl text-surface-300 dark:text-surface-600 mb-4"
          ></i>
          <p class="text-surface-500 dark:text-surface-400 text-lg">
            No payouts found
          </p>
          <p class="text-surface-400 dark:text-surface-500 text-sm">
            Choose a supplier or adjust your filters
          </p>
        </div>
      </template>
    </DataTable>

    <!-- Bank Account Assignment Dialog -->
    <BankAccountAssignmentDialog
      v-if="assignmentDialogPayout"
      v-model:visible="showAssignmentDialog"
      :payout-id="assignmentDialogPayout.Id"
      :merchant-id="getPayoutMerchantId(assignmentDialogPayout)"
      :store-id="assignmentDialogPayout.StoreId"
      :business-entity-id="assignmentDialogPayout.BusinessEntityId"
      @assigned="handleBankAccountAssigned"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue'
import { FilterMatchMode } from '@primevue/core/api'
import { useRoute, useRouter } from 'vue-router'
import { useFrontegg } from '@frontegg/vue'
import { useToast } from 'primevue/usetoast'
import { storeToRefs } from 'pinia'
import { useDebounceFn } from '@vueuse/core'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Skeleton from 'primevue/skeleton'
import InputText from 'primevue/inputtext'
import MultiSelect from 'primevue/multiselect'
import Select from 'primevue/select'
import Button from 'primevue/button'
import { BanknotesIcon } from '@heroicons/vue/24/outline'
import BadgePayoutStatus from '@/components/common/badges/BadgePayoutStatus.vue'
import BadgeBankAccountAssignmentState from '@/components/common/badges/BadgeBankAccountAssignmentState.vue'
import BankAccountAssignmentDialog from '@/components/shopifypayouts/BankAccountAssignmentDialog.vue'
import { formatDateTimeForDisplay } from '@/util/dateUtils'
import { formatCurrency } from '@/util/util'
import { useMerchantStore } from '@/stores/merchantStore'
import { useShopifyPayoutsStore } from '@/stores/shopifyPayoutsStore'
import { errorToastWithDetails } from '@/util/toasts'
import { normalizeError } from '@/util/errorNormalizer'
import type {
  ShopifyPayoutSummary,
  ShopifyPayoutFilterParams,
  ShopifyPayoutStatus,
} from '@/types/shopifyPayouts'

interface Props {
  merchantId: string | null
  showMerchantColumn?: boolean
  usePendingPayouts?: boolean // If true, fetch pending payouts instead of regular payouts
  showFilters?: boolean // If false, hide all filters
  initialStoreId?: string | null // Pre-filter to a specific store
}

const props = withDefaults(defineProps<Props>(), {
  merchantId: null,
  showMerchantColumn: false,
  usePendingPayouts: false,
  showFilters: true,
  initialStoreId: null,
})

const emit = defineEmits<{
  'row-select': [payout: ShopifyPayoutSummary]
}>()

const route = useRoute()
const router = useRouter()
const toast = useToast()
const { authState } = useFrontegg()
const merchantStore = useMerchantStore()
const store = useShopifyPayoutsStore()

const {
  payouts,
  paginationInfo,
  isLoading,
  pendingPayouts,
  isLoadingPendingPayouts,
  pendingPayoutsTotalElements,
  pendingPayoutsTotalPages,
  pendingPayoutsCurrentPage,
  pendingPayoutsPageSize,
} = storeToRefs(store)

const initialLoading = ref(true)
const isLoadingPayouts = ref(false)

// Bank Account Assignment Dialog state
const showAssignmentDialog = ref(false)
const assignmentDialogPayout = ref<ShopifyPayoutSummary | null>(null)

// Sort state - default to IssuedAt descending (most recent first)
const currentSortField = ref('IssuedAt')
const currentSortOrder = ref(-1)

// Filter state for PrimeVue DataTable (separate from props.filters)
function createEmptyFilters(): Record<
  string,
  { value: unknown; matchMode: string }
> {
  return {
    Id: { value: null, matchMode: FilterMatchMode.EQUALS },
    Status: { value: null, matchMode: FilterMatchMode.IN },
    StoreId: { value: null, matchMode: FilterMatchMode.EQUALS },
    BusinessEntityId: { value: null, matchMode: FilterMatchMode.EQUALS },
    BankAccountId: { value: null, matchMode: FilterMatchMode.EQUALS },
  }
}

function resetTableFilters() {
  tableFilters.value = createEmptyFilters()
  store.resetFilters()
}

const tableFilters = ref(createEmptyFilters())

// Debounced filter callbacks
const debouncedIdFilter = useDebounceFn((filterCallback: () => void) => {
  filterCallback()
}, 500)

const debouncedStatusFilter = useDebounceFn((filterCallback: () => void) => {
  filterCallback()
}, 500)

const debouncedStoreIdFilter = useDebounceFn((filterCallback: () => void) => {
  filterCallback()
}, 500)

const debouncedBusinessEntityIdFilter = useDebounceFn(
  (filterCallback: () => void) => {
    filterCallback()
  },
  500,
)

const debouncedBankAccountIdFilter = useDebounceFn(
  (filterCallback: () => void) => {
    filterCallback()
  },
  500,
)

// Status options for filter dropdown
const statusOptions = [
  { label: 'Paid', value: 'PAID' },
  { label: 'In Transit', value: 'IN_TRANSIT' },
  { label: 'Scheduled', value: 'SCHEDULED' },
  { label: 'Failed', value: 'FAILED' },
  { label: 'Canceled', value: 'CANCELED' },
] as const

// Computed options for filter dropdowns based on merchant store data
const storeIdOptions = computed(() => {
  if (!props.merchantId) return []
  const merchant = merchantStore
    .getMerchants()
    .find(m => m.Id === props.merchantId)
  if (!merchant?.ShopifyStores) return []

  return merchant.ShopifyStores.filter(store => store.Id)
    .map(store => ({ label: store.Id as string, value: store.Id as string }))
    .sort((a, b) => a.label.localeCompare(b.label))
})

const businessEntityIdOptions = computed(() => {
  if (!props.merchantId) return []
  const merchant = merchantStore
    .getMerchants()
    .find(m => m.Id === props.merchantId)
  if (!merchant?.ShopifyStores) return []

  const entities: { label: string; value: string }[] = []
  for (const store of merchant.ShopifyStores) {
    if (!store.BusinessEntities) continue
    for (const entity of store.BusinessEntities) {
      entities.push({ label: entity.DisplayName, value: entity.Id })
    }
  }
  return entities.sort((a, b) => a.label.localeCompare(b.label))
})

const bankAccountIdOptions = computed(() => {
  if (!props.merchantId) return []
  const merchant = merchantStore
    .getMerchants()
    .find(m => m.Id === props.merchantId)
  if (!merchant?.ShopifyStores) return []

  const accounts: { label: string; value: string }[] = []
  for (const store of merchant.ShopifyStores) {
    if (!store.BusinessEntities) continue
    for (const entity of store.BusinessEntities) {
      if (!entity.BankAccounts) continue
      for (const account of entity.BankAccounts) {
        accounts.push({
          label: account.BankName || account.Id,
          value: account.Id,
        })
      }
    }
  }
  return accounts.sort((a, b) => a.label.localeCompare(b.label))
})

// Check if we're on a payouts route
const isPayoutsRoute = computed(() => {
  const routeName = route.name as string
  return (
    routeName === 'shopifyStoresPayouts' ||
    routeName === 'shopifyStoresPayoutDetail' ||
    routeName === 'shopifyStoresBalanceTransactionDetail' ||
    routeName === 'payoutsPendingAssignment'
  )
})

// Get display name for business entity
function getBusinessEntityDisplayName(businessEntityId: string): string {
  return store.getBusinessEntityDisplayName(businessEntityId, props.merchantId)
}
// Determine when to show skeleton loading
const showSkeletonLoading = computed(
  () =>
    initialLoading.value || effectiveIsLoading.value || isLoadingPayouts.value,
)

// Create mock data for skeleton loading
function createMockPayout(index: number): ShopifyPayoutSummary {
  return {
    Id: index,
    StoreId: '',
    BankAccountId: null,
    BusinessEntityId: '',
    Amount: 0,
    Currency: 'USD',
    Status: 'SCHEDULED',
    IssuedAt: '',
    TransactionType: '',
  }
}

// Create skeleton rows when loading
const skeletonRows = computed(() => {
  const length = effectivePaginationInfo.value?.pageSize || 20
  return Array.from({ length }, (_, index) => createMockPayout(index))
})

// Return skeleton rows when loading, real data otherwise
const displayData = computed(() => {
  if (showSkeletonLoading.value) return skeletonRows.value
  return props.usePendingPayouts ? pendingPayouts.value : payouts.value
})

// Use pending payouts pagination info when in pending mode
const effectivePaginationInfo = computed(() => {
  if (props.usePendingPayouts) {
    return {
      page: pendingPayoutsCurrentPage.value,
      pageSize: pendingPayoutsPageSize.value,
      total: pendingPayoutsTotalElements.value,
      totalPages: pendingPayoutsTotalPages.value,
    }
  }
  return paginationInfo.value
})

const effectiveIsLoading = computed(() => {
  return props.usePendingPayouts
    ? isLoadingPendingPayouts.value
    : isLoading.value
})

function formatTransactionType(type: string): string {
  return store.formatTransactionType(type)
}

// Check if user can assign bank account to a specific payout
function canAssignBankAccount(payout: ShopifyPayoutSummary): boolean {
  return store.canAssignBankAccount(payout)
}

// Supported sortable fields (matching backend database fields)
const SORTABLE_FIELDS = ['Id', 'Status', 'IssuedAt'] as const
type SortableField = (typeof SORTABLE_FIELDS)[number]

// Convert PrimeVue sort format to API format
function formatSortForApi(sortField: string, sortOrder: number): string {
  // Validate that the field is sortable
  if (!SORTABLE_FIELDS.includes(sortField as SortableField)) {
    // Fallback to default sort if invalid field
    return 'issuedAt,desc'
  }

  // Map field names to API format (camelCase)
  const fieldMap: Record<string, string> = {
    Id: 'id',
    Status: 'status',
    IssuedAt: 'issuedAt',
  }

  const apiFieldName = fieldMap[sortField] || sortField.toLowerCase()
  const direction = sortOrder === -1 ? 'desc' : 'asc'
  return `${apiFieldName},${direction}`
}

async function loadPayouts(filters?: ShopifyPayoutFilterParams) {
  // Prevent concurrent calls
  const wasAlreadyLoading = isLoadingPayouts.value
  isLoadingPayouts.value = true
  if (wasAlreadyLoading) {
    return
  }

  // Handle pending payouts mode (no merchant ID required)
  if (props.usePendingPayouts) {
    try {
      // Extract sort from filters if provided, otherwise use current UI sort state
      let sortParam: string
      if (filters?.sort) {
        sortParam = filters.sort
        const [fieldName, direction] = filters.sort.split(',')
        const apiToPrimeVueMap: Record<string, string> = {
          id: 'Id',
          status: 'Status',
          issuedAt: 'IssuedAt',
        }
        const primeVueField = apiToPrimeVueMap[fieldName] || fieldName
        if (SORTABLE_FIELDS.includes(primeVueField as SortableField)) {
          currentSortField.value = primeVueField
          currentSortOrder.value = direction === 'desc' ? -1 : 1
        }
      } else {
        sortParam = formatSortForApi(
          currentSortField.value,
          currentSortOrder.value,
        )
      }

      const { sort: _sort, ...filtersWithoutSort } = filters || {}
      void _sort

      const pageToUse = filters?.page ?? pendingPayoutsCurrentPage.value
      const sizeToUse = filters?.size ?? pendingPayoutsPageSize.value

      await store.fetchPendingPayouts(authState, {
        page: pageToUse,
        size: sizeToUse,
        sort: sortParam,
        ...filtersWithoutSort,
      })
    } catch (error: unknown) {
      const apiError = normalizeError(error)
      errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
    } finally {
      initialLoading.value = false
      isLoadingPayouts.value = false
    }
    return
  }

  const merchantIdToUse = props.merchantId
  if (!merchantIdToUse) {
    initialLoading.value = false
    isLoadingPayouts.value = false
    return
  }

  try {
    // Extract sort from filters if provided, otherwise use current UI sort state
    let sortParam: string
    if (filters?.sort) {
      // Filters provided sort - use it and sync UI state
      sortParam = filters.sort
      // Parse API format "fieldName,direction" back to PrimeVue format
      const [fieldName, direction] = filters.sort.split(',')
      // Map API field names (camelCase) to PrimeVue field names (PascalCase)
      const apiToPrimeVueMap: Record<string, string> = {
        id: 'Id',
        status: 'Status',
        issuedAt: 'IssuedAt',
      }
      const primeVueField = apiToPrimeVueMap[fieldName] || fieldName
      // Only update UI state if it's a valid sortable field
      if (SORTABLE_FIELDS.includes(primeVueField as SortableField)) {
        currentSortField.value = primeVueField
        currentSortOrder.value = direction === 'desc' ? -1 : 1
      }
    } else {
      // Use current UI sort state
      sortParam = formatSortForApi(
        currentSortField.value,
        currentSortOrder.value,
      )
    }

    // Extract sort from filters to avoid duplicate
    const { sort: _sort, ...filtersWithoutSort } = filters || {}
    void _sort

    await store.fetchPayouts(authState, merchantIdToUse, {
      page: 1,
      size: store.pageSize,
      sort: sortParam,
      ...filtersWithoutSort,
    })
  } catch (error: unknown) {
    const apiError = normalizeError(error)
    errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
  } finally {
    initialLoading.value = false
    isLoadingPayouts.value = false
  }
}

async function onPageChange(event: { page: number; rows: number }) {
  const newPage = event.page + 1 // PrimeVue uses 0-based indexing

  await loadPayouts({
    page: newPage,
    size: event.rows,
    ...convertFiltersToApiParams(tableFilters.value),
  })
}

function onSortChange(event: { sortField: string; sortOrder: number }) {
  // Only allow sorting on supported fields
  if (!SORTABLE_FIELDS.includes(event.sortField as SortableField)) {
    // Reset to default sort if invalid field
    currentSortField.value = 'IssuedAt'
    currentSortOrder.value = -1
    return
  }

  // Update local sort state
  currentSortField.value = event.sortField
  currentSortOrder.value = event.sortOrder

  // Reload payouts with new sort
  if (props.merchantId && isPayoutsRoute.value) {
    const sortParam = formatSortForApi(event.sortField, event.sortOrder)
    loadPayouts({
      ...props.filters,
      sort: sortParam,
    })
  }
}

function onRowClick(event: { data: ShopifyPayoutSummary }) {
  if (!showSkeletonLoading.value) {
    emit('row-select', event.data)
  }
}

function navigateToMerchantConfig(merchantId: string) {
  router.push({
    name: 'shopifyStoresPayouts',
    params: { id: merchantId },
  })
}

function openAssignmentDialog(payout: ShopifyPayoutSummary) {
  assignmentDialogPayout.value = payout
  showAssignmentDialog.value = true
}

function getPayoutMerchantId(payout: ShopifyPayoutSummary): string {
  // For pending payouts, use the MerchantId from the payout
  // For regular payouts, use the prop merchantId
  return payout.MerchantId || props.merchantId || ''
}

async function handleBankAccountAssigned() {
  showAssignmentDialog.value = false
  assignmentDialogPayout.value = null
  await refresh()
}

// Convert PrimeVue filters to API filter parameters
function convertFiltersToApiParams(
  primeVueFilters: Record<string, { value: unknown; matchMode: string }>,
): Partial<ShopifyPayoutFilterParams> {
  const apiParams: Partial<ShopifyPayoutFilterParams> = {}

  // Convert Id filter (InputText returns string)
  const rawId = primeVueFilters.Id?.value as string | null | undefined
  const normalizedId =
    typeof rawId === 'string' && rawId.trim().length > 0
      ? rawId.trim()
      : undefined
  if (normalizedId) {
    apiParams.id = normalizedId
  }

  // Convert Status filter (MultiSelect returns array)
  if (primeVueFilters.Status?.value) {
    const statusValues = primeVueFilters.Status.value as ShopifyPayoutStatus[]
    if (statusValues.length > 0) {
      apiParams.status = statusValues
    }
  }

  // Convert StoreId filter (Select returns single value)
  if (primeVueFilters.StoreId?.value) {
    apiParams.storeId = primeVueFilters.StoreId.value as string
  }

  // Convert BusinessEntityId filter (Select returns single value)
  if (primeVueFilters.BusinessEntityId?.value) {
    apiParams.businessEntityId = primeVueFilters.BusinessEntityId
      .value as string
  }

  // Convert BankAccountId filter (Select returns single value)
  if (primeVueFilters.BankAccountId?.value) {
    apiParams.bankAccountId = primeVueFilters.BankAccountId.value as string
  }

  return apiParams
}

// Handle filter changes
function onFilterChange() {
  if (!props.merchantId || !isPayoutsRoute.value) return

  // Convert PrimeVue filters to API parameters
  const apiFilters = convertFiltersToApiParams(tableFilters.value)

  // Store filters in the store for persistence
  store.setFilters(apiFilters)

  // Reload payouts with current table filters
  loadPayouts(apiFilters)
}

// Expose refresh method for parent components
async function refresh() {
  await loadPayouts(convertFiltersToApiParams(tableFilters.value))
}

defineExpose({ refresh })

// Load payouts on mount if on payouts route
onMounted(async () => {
  // Clear any stale store filters so they don't silently filter data
  // when the component is freshly created with empty UI filter inputs
  resetTableFilters()

  // Apply initial store filter if provided
  if (props.initialStoreId) {
    tableFilters.value.StoreId.value = props.initialStoreId
    store.setFilters({ storeId: props.initialStoreId })
  }

  if (props.usePendingPayouts) {
    await loadPayouts()
  } else if (isPayoutsRoute.value && props.merchantId) {
    await loadPayouts(convertFiltersToApiParams(tableFilters.value))
  } else {
    initialLoading.value = false
  }
})

// Watch for route changes to reload payouts
watch(
  () => [
    route.name,
    props.merchantId,
    store.selectedMerchantId,
    props.usePendingPayouts,
  ],
  async ([routeName, merchantId, selectedMerchantId, usePendingPayouts]) => {
    const isPayoutsRouteNow =
      routeName === 'shopifyStoresPayouts' ||
      routeName === 'shopifyStoresPayoutDetail' ||
      routeName === 'shopifyStoresBalanceTransactionDetail' ||
      routeName === 'payoutsPendingAssignment'

    if (usePendingPayouts && routeName === 'payoutsPendingAssignment') {
      await loadPayouts()
      return
    }

    if (isPayoutsRouteNow && merchantId) {
      const merchantIdChanged =
        selectedMerchantId !== merchantId ||
        store.currentMerchantId !== merchantId
      const shouldReload =
        routeName === 'shopifyStoresPayouts' || merchantIdChanged

      if (shouldReload) {
        if (merchantIdChanged) {
          resetTableFilters()
        }
        await loadPayouts()
      }
    }
  },
)

// Watch for merchant ID changes to reload payouts
watch(
  () => props.merchantId,
  async (newMerchantId, oldMerchantId) => {
    // If merchant ID changed and we're on a payouts route, reload data
    if (
      newMerchantId &&
      newMerchantId !== oldMerchantId &&
      isPayoutsRoute.value
    ) {
      resetTableFilters()
      await loadPayouts()
    }
  },
)
</script>

<style scoped>
:deep(.p-datatable-tbody > tr) {
  cursor: pointer;
}
</style>
